import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { IS_PUBLIC_APPLICATION } from '@ddt-web/config';
import { WebUserDataConstants } from '@ddt-web/domain';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { SharedQueryParamKey, UrlUtils } from '@ddt-web/url';
import { UserDataService } from '@ddt-web/user-data';
import * as assert from 'assert';
import * as get from 'lodash/get';
import { Observable } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs/operators';

import { AuthUtil } from '../auth/auth.util';
import { OktaAuthenticationService } from '../auth/okta-authentication.service';

@Injectable()
export class OktaInterceptorService implements HttpInterceptor {
	@InjectQueryParam(SharedQueryParamKey.API_OVERRIDE) public readonly overrideParam: string;
	// List of Okta requests that should not trigger the interceptor (request url includes)
	private readonly BYPASS_INTERCEPTOR_REQUESTS = ['sessions/me', 'v1/authorize', 'v1/token', '/assets/environment', 'blk-one-c-assets', 'public-models', '.jsn'];
	// TODO rohyadav: Needs to be fixed but for local TPM testing remove everything in this array

	private readonly PUBLIC_REQUESTS = ['model-families'];
	// List of requests that should not be affected by the ?apiOverride flag (request pathname equals)
	private readonly IGNORE_OVERRIDE_REQUESTS = [WebUserDataConstants.TOOL_SERVICE_URL.GET_USER];
	// List of requests that require additional headers (request url includes)
	private readonly REQUIRE_USER_ATTRIBUTE_REQUEST = ['/eii-services'];

	constructor(
		@Inject('environment') private environment: any,
		private servicePathProvider: ServicePathProviderService,
		private oktaAuthenticationService: OktaAuthenticationService,
		private userDataService: UserDataService,
		@Optional() @Inject(IS_PUBLIC_APPLICATION) private isPublicApp: boolean
	) { }

	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const shouldBypassInterceptor = this.BYPASS_INTERCEPTOR_REQUESTS.some(oktaRequest => request.url.includes(oktaRequest)) || !AuthUtil.isOktaEnabled(this.environment.cloud);
		const isPublicRequest = this.isPublicRequestUrl(request.url);

		if (shouldBypassInterceptor) {
			return next.handle(request);
		} else if ((this.isPublicApp && isPublicRequest) || UrlUtils.isInternalHost()) {
			return this.handleRequest(next, request);
		} else {
			return this.oktaAuthenticationService.isAuthenticated$.pipe(
				first(isAuthenticated => isAuthenticated),
				switchMap(() => {
					return this.handleRequest(next, request);
				})
			);
		}
	}

	private handleRequest(next: HttpHandler, request: HttpRequest<any>): Observable<HttpEvent<any>> {
		const shouldIgnoreOverride = this.IGNORE_OVERRIDE_REQUESTS.some(pathToIgnore => {
			const path = new URL(request.url).pathname.replace('/api-gateway', '');
			return path === pathToIgnore;
		});
		if (this.overrideParam && !shouldIgnoreOverride) {
			return this.handlePreviewFlagRequest().pipe(first(), mergeMap(isPreviewRequest => {
				return next.handle(this.interceptOktaRequest(request, isPreviewRequest));
			}));
		} else {
			return next.handle(this.interceptOktaRequest(request, UrlUtils.isPreview()));
		}
	}

	private interceptOktaRequest(request: HttpRequest<any>, isPreview: boolean): HttpRequest<any> {
		const headers = {};
		const isPublicRequest = this.isPublicApiRequest(request);

		if ((!isPublicRequest || AuthUtil.isPublicAppLogin()) && !UrlUtils.isInternalHost()) {
			const accessToken = this.oktaAuthenticationService.getAccessToken();
			assert(!!accessToken, 'Access token is required');
			headers['Authorization'] = `Bearer ${accessToken}`;
		}

		headers['x-api-key'] = this.oktaAuthenticationService.getApiKey();

		// When accessing pods directly, add a header to allow APIs to route their traffic accordingly
		if (UrlUtils.isProdCloud()) {
			headers['x-target-ref'] = 'staging';
		}

		if (isPreview) {
			headers['prefer'] = 'preview';
		}

		// Waris: temporary naming and stuff, this is not true true external, but services that will required different headers options
		const isExternalRequest = this.REQUIRE_USER_ATTRIBUTE_REQUEST.some(userAttributeRequest => request.url.includes(userAttributeRequest));
		if (isExternalRequest) {
			headers['x-user-attributes'] = 'firstName,lastName,loginEmail,login,accessLevelUS,authType,firmName,primaryCountryOfResidence, accessFlagBetaCompare,accessFlagPretrade,pretradePermGroup,liquidBetaAccessFlag,FactorWorkbenchAccessFlag';
			headers['id-token'] = this.oktaAuthenticationService.getIdToken();
		} else if (UrlUtils.isLocalhostOrTest() || UrlUtils.isInternalHost()) {
			headers['x-api-target'] = 'hybrid-amer'; // Specifically target the Apigee Hybrid instance
		}

		return request.clone({
			url: this.getInterceptedUrl(request),
			setHeaders: headers,
			withCredentials: true
		});
	}

	private getInterceptedUrl(request: HttpRequest<any>): string {
		const apiGatewayUrl = this.servicePathProvider.getServicePath('api-gateway');
		const currentUrl = request.url;
		const isApiGatewayRequest = currentUrl.includes(apiGatewayUrl);
		if (isApiGatewayRequest) {
			return this.convertApiGatewayToApigeeUrl(currentUrl);
		}
		return currentUrl;
	}

	private handlePreviewFlagRequest(): Observable<boolean> {
		const isPreview = UrlUtils.isPreview();

		// If there's an override param, check first that the user is internal 
		return this.userDataService.isInternalUser$().pipe(map(isInternalUser => {
			let isPreviewOverride;
			if ((UrlUtils.isProdDmz() || UrlUtils.isAzrc() || UrlUtils.isProdCloud() || UrlUtils.isTestCloud()) && this.overrideParam === 'preview') { // if we're in prod DMZ but want to view preview APIs
				isPreviewOverride = true;
			}

			if (isPreview && this.overrideParam === 'prod') { // if we're in preview but want to view prod APIs
				isPreviewOverride = false;
			}
			return (isInternalUser && isPreviewOverride) || (!isInternalUser && isPreview); // if an external user tries to use the override, fall back to isPreview
		}));
	}

	/**
	 * Convert existing API nautilus requests into apigee requests. This is temporarily present to support both bare metal and cloud.
	 */
	private convertApiGatewayToApigeeUrl(currentUrl: string): string {
		const apiGatewayUrl = this.servicePathProvider.getServicePath('api-gateway');
		const apigeeUrl = this.servicePathProvider.getServicePath('apigee-path') ?? 'https://api-test.blackrock.com';

		if (currentUrl.includes('/wds/')) {
			return currentUrl.replace(apiGatewayUrl + '/wds/', apigeeUrl + '/wealth/data/');
		} else if (currentUrl.includes('/iretire-server/')) {
			return currentUrl.replace(apiGatewayUrl + '/iretire-server/', apigeeUrl + '/wealth/iretire/');
		} else if (currentUrl.includes('/advisor-center/')) {
			return currentUrl.replace(apiGatewayUrl + '/advisor-center/', apigeeUrl + '/wealth/advisor-center/');
		} else if (currentUrl.includes('/wealth-web/')) {
			return currentUrl.replace(apiGatewayUrl + '/wealth-web/', apigeeUrl + '/wealth/web/');
		} else if (currentUrl.includes('/eii-services/')) {
			return currentUrl.replace(apiGatewayUrl + '/eii-services/', apigeeUrl + '/eii-services/');
		}

		return currentUrl;
	}

	/**
	 * This function checks if the http request is one of the public request from it's url
	 * @param url 
	 * @returns 
	 */
	private isPublicRequestUrl(url: string): boolean {
		return this.PUBLIC_REQUESTS.some(publicRequest => url.includes(publicRequest));
	}

	/**
	 * This function checks if the http request is public depending on the request url and CwpUserSignedIn
	 * @param request 
	 * @returns 
	 */
	private isPublicApiRequest(request: HttpRequest<any>): boolean {
		const isPublicRequestUrl = this.isPublicRequestUrl(request.url);
		const isCwpUserSignedIn = get(window, 'BLK.isCwpUserSignedIn', false);
		return (isPublicRequestUrl && !isCwpUserSignedIn);
	}

}
